import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  CheckCircle,
  Star,
  Zap,
  Crown,
  Gem,
  MessageCircle
} from "lucide-react";

const PlanosPage = () => {
  const plans = [
    {
      icon: Star,
      name: "Essencial",
      price: "497",
      tagline: "Para começar com direção",
      description: "Ideal para empresas que estão iniciando a estruturação da área de pessoas e precisam de orientação estratégica.",
      forWho: "Pequenas empresas ou empreendedores que querem organizar os primeiros processos de gestão de pessoas.",
      features: [
        "Diagnóstico inicial da empresa",
        "1 reunião mensal de acompanhamento (1h)",
        "Suporte por e-mail",
        "Orientações estratégicas mensais",
        "Material de apoio básico"
      ],
      support: "Acompanhamento mensal com suporte assíncrono por e-mail.",
      highlight: false
    },
    {
      icon: Zap,
      name: "Crescimento",
      price: "997",
      tagline: "Para escalar com qualidade",
      description: "Para empresas em expansão que precisam desenvolver lideranças e estruturar processos mais robustos.",
      forWho: "Empresas em crescimento com equipes de 5 a 20 pessoas que precisam de estrutura e desenvolvimento.",
      features: [
        "Tudo do plano Essencial",
        "2 reuniões mensais de acompanhamento",
        "1 treinamento trimestral para equipe",
        "Suporte prioritário por WhatsApp",
        "Relatórios de progresso",
        "Consultoria em processos básicos"
      ],
      support: "Acompanhamento quinzenal com suporte prioritário via WhatsApp.",
      highlight: true
    },
    {
      icon: Crown,
      name: "Performance",
      price: "1.997",
      tagline: "Para alta performance",
      description: "Para organizações que buscam alto desempenho consistente e desenvolvimento contínuo de equipes.",
      forWho: "Empresas consolidadas que querem elevar o padrão de liderança e engajamento das equipes.",
      features: [
        "Tudo do plano Crescimento",
        "4 reuniões mensais de acompanhamento",
        "1 treinamento mensal para equipe",
        "Avaliações comportamentais de equipe",
        "Pesquisa de clima organizacional",
        "Plano de desenvolvimento individual",
        "Suporte ilimitado"
      ],
      support: "Acompanhamento semanal com suporte ilimitado e atendimento prioritário.",
      highlight: false
    },
    {
      icon: Gem,
      name: "Estratégico Premium",
      price: "3.497",
      tagline: "Transformação completa",
      description: "Acompanhamento completo e personalizado para empresas que buscam transformação profunda e sustentável.",
      forWho: "Empresas que precisam de parceria estratégica contínua para transformação cultural e organizacional.",
      features: [
        "Tudo do plano Performance",
        "Acompanhamento semanal dedicado",
        "Consultoria estratégica personalizada",
        "Treinamentos ilimitados sob demanda",
        "Recrutamento e seleção inclusos",
        "Mentoria para lideranças",
        "Acesso direto à consultora",
        "Projetos especiais personalizados"
      ],
      support: "Parceria estratégica com acompanhamento semanal e acesso direto à consultora.",
      highlight: false
    }
  ];

  const faqs = [
    {
      question: "Posso personalizar o plano conforme minha necessidade?",
      answer: "Sim! Todos os planos podem ser adaptados à realidade da sua empresa. Após o diagnóstico inicial, criamos uma proposta personalizada que atenda às suas necessidades específicas."
    },
    {
      question: "Como funciona o diagnóstico inicial?",
      answer: "O diagnóstico é uma análise profunda da sua empresa: cultura, processos, desafios e objetivos. A partir dele, desenhamos o plano de ação mais adequado para sua realidade."
    },
    {
      question: "Posso mudar de plano depois?",
      answer: "Sim, você pode fazer upgrade ou ajuste de plano a qualquer momento, conforme a evolução das necessidades da sua empresa."
    },
    {
      question: "Os treinamentos são presenciais ou online?",
      answer: "Trabalhamos em ambos os formatos. A definição é feita conforme a localização da empresa e as necessidades específicas de cada treinamento."
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="planos-page">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-brand-base-clara via-[#E5DBC5] to-brand-surface relative overflow-hidden" data-testid="planos-hero">
        {/* Decorative lines */}
        <div className="absolute right-12 lg:right-24 top-1/4 bottom-1/4 flex gap-3">
          <div className="w-[2px] h-full bg-brand-terra-humana/20" />
          <div className="w-[2px] h-full bg-brand-terracota/30 mt-8" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-6 flex items-center gap-3 justify-center">
              <span className="w-6 h-[1px] bg-brand-terracota" />
              PLANOS DE ACOMPANHAMENTO
              <span className="w-6 h-[1px] bg-brand-terracota" />
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-brand-neutro leading-[1.1] mb-6">
              Escolha o nível de suporte ideal para sua empresa.
            </h1>
            <p className="font-sans text-lg lg:text-xl text-brand-muted leading-relaxed">
              Planos flexíveis que crescem junto com sua organização. Cada plano pode ser personalizado conforme suas necessidades.
            </p>
          </div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="py-16 lg:py-24 bg-brand-surface" data-testid="planos-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`bg-brand-base-clara rounded-2xl overflow-hidden flex flex-col ${
                  plan.highlight 
                    ? 'ring-2 ring-brand-terracota relative' 
                    : 'border border-brand-border'
                }`}
                data-testid={`plan-${plan.name.toLowerCase()}`}
              >
                {plan.highlight && (
                  <div className="absolute -top-px left-0 right-0 h-1 bg-brand-terracota" />
                )}
                
                {/* Plan Header */}
                <div className={`p-6 lg:p-8 ${plan.highlight ? 'bg-brand-terracota/5' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      plan.highlight ? 'bg-brand-terracota' : 'bg-brand-terra-humana/20'
                    }`}>
                      <plan.icon className={plan.highlight ? 'text-brand-base-clara' : 'text-brand-terracota'} size={20} />
                    </div>
                    {plan.highlight && (
                      <span className="bg-brand-terracota text-brand-base-clara text-xs font-semibold px-3 py-1 rounded-full">
                        Mais Popular
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-serif text-2xl font-medium text-brand-neutro mb-1">
                    {plan.name}
                  </h3>
                  <p className="font-sans text-sm text-brand-terracota mb-4">
                    {plan.tagline}
                  </p>
                  
                  <div className="mb-4">
                    <span className="font-sans text-sm text-brand-muted">R$</span>
                    <span className="font-serif text-4xl lg:text-5xl font-semibold text-brand-neutro">{plan.price}</span>
                    <span className="font-sans text-sm text-brand-muted">/mês</span>
                  </div>
                  
                  <p className="font-sans text-sm text-brand-muted leading-relaxed">
                    {plan.description}
                  </p>
                </div>

                {/* Plan Content */}
                <div className="p-6 lg:p-8 pt-0 flex-1 flex flex-col">
                  {/* Para quem */}
                  <div className="mb-6">
                    <p className="font-sans text-xs uppercase tracking-wider text-brand-terracota mb-2">
                      Para quem é
                    </p>
                    <p className="font-sans text-sm text-brand-neutro leading-relaxed">
                      {plan.forWho}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="mb-6 flex-1">
                    <p className="font-sans text-xs uppercase tracking-wider text-brand-terracota mb-3">
                      O que está incluso
                    </p>
                    <ul className="space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="text-brand-verde flex-shrink-0 mt-0.5" size={16} />
                          <span className="font-sans text-sm text-brand-neutro">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Support Level */}
                  <div className="mb-6 p-4 bg-brand-surface rounded-xl">
                    <p className="font-sans text-xs uppercase tracking-wider text-brand-muted mb-1">
                      Nível de acompanhamento
                    </p>
                    <p className="font-sans text-sm text-brand-neutro">
                      {plan.support}
                    </p>
                  </div>

                  {/* CTA */}
                  <a
                    href={`https://wa.me/5586998325746?text=Olá! Tenho interesse no plano ${plan.name}. Gostaria de saber mais detalhes.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-sans text-sm font-medium transition-premium ${
                      plan.highlight 
                        ? 'btn-primary' 
                        : 'btn-secondary'
                    }`}
                    data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                  >
                    Quero este plano
                    <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Personalization Note */}
          <div className="mt-12 text-center">
            <p className="font-sans text-brand-muted">
              <span className="text-brand-terracota font-medium">Nota:</span> Todos os planos podem ser personalizados conforme a necessidade da sua empresa.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-brand-base-clara" data-testid="planos-faq">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-brand-terracota mb-4">
              Dúvidas Frequentes
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl font-medium text-brand-neutro">
              Perguntas sobre os planos
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-brand-surface p-6 lg:p-8 rounded-2xl"
                data-testid={`faq-${index}`}
              >
                <h3 className="font-serif text-lg font-medium text-brand-neutro mb-3">
                  {faq.question}
                </h3>
                <p className="font-sans text-brand-muted leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-brand-verde" data-testid="planos-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <MessageCircle className="text-brand-base-clara/50 mx-auto mb-6" size={48} strokeWidth={1} />
          <h2 className="font-serif text-3xl lg:text-4xl font-medium text-brand-base-clara mb-6">
            Não tem certeza qual plano é ideal?
          </h2>
          <p className="font-sans text-lg text-brand-base-clara/80 leading-relaxed mb-10 max-w-2xl mx-auto">
            Agende uma conversa gratuita. Vamos entender sua realidade e indicar o plano mais adequado para o momento da sua empresa.
          </p>
          <a
            href="https://wa.me/5586998325746?text=Olá! Gostaria de uma orientação para escolher o plano ideal para minha empresa."
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center gap-2 bg-brand-base-clara text-brand-neutro hover:bg-white px-8 py-4 rounded-full font-sans font-medium transition-premium"
            data-testid="planos-cta-button"
          >
            Falar com a Consultora
            <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default PlanosPage;
